<template>
  <div>
    <input
      v-model="email"
      type="email"
      class="form-control"
      autocomplete="nope"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      @blur="() => checkEmail(true)"
    >
    <span
      v-if="!!invalidErrorMessage"
      class="help-block text-red"
    >
      {{ invalidErrorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, WritableComputedRef, watch } from 'vue';

import { useGlobal } from '@/use/global';

const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const { verifyEmail } = useGlobal();

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },

  placeholder: {
    type: String,
    default: 'Enter an email',
  },

  required: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  skipEmailValidation: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'update-validation']);

const isValid = ref(true);
const invalidErrorMessage = ref('');
const isLoading = ref(false);

const email: WritableComputedRef<string> = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const checkEmail = async (isFullChecking: boolean = false) => {
  if (isFullChecking) {
    email.value = email.value?.trim(); // Trim the email before validation by the backend. Some browsers do it implicitly for <input type="email" />, but not all.
  }

  if (!email.value) {
    isValid.value = true;
    invalidErrorMessage.value = '';
    return emit('update-validation', isValid.value);
  }

  if (!emailPattern.test(email.value)) {
    isValid.value = false;
    invalidErrorMessage.value = 'Email is invalid.';
    return emit('update-validation', isValid.value);
  }

  isValid.value = true;
  invalidErrorMessage.value = '';

  if (!isFullChecking) {
    return emit('update-validation', isValid.value);
  }

  if (props.skipEmailValidation) {
    return emit('update-validation', isValid.value);
  }

  isLoading.value = true;
  try {
    const response = await verifyEmail(email.value);

    if (response.result === 'undeliverable' || response.result === 'risky') {
      isValid.value = false;
      invalidErrorMessage.value = 'Email is undeliverable.';
    }
  } catch (error) {
    if (error instanceof Error && error.message === 'Invalid email') {
      isValid.value = false;
      invalidErrorMessage.value = 'Email is invalid.';
    }
    // no need to do additional working. b/c we check the email in the background
  }
  isLoading.value = false;

  return emit('update-validation', isValid.value);
};

watch(email, () => checkEmail(), {
  immediate: true,
});
</script>
